


// src/SideBarComponents/DataSources/ValidationResultPanel.tsx

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  Divider,
  Button,
  Menu,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { format, parseISO, isValid } from 'date-fns';
import ErrorDisplay from './ErrorDisplay'; // Assumes ErrorDisplay.tsx is in the same directory

// --- INTERFACES AND CONSTANTS ---

export interface ValidationDataSource {
  id: string;
  name: string;
  billingAccountId: string;
  validationStatus: 'Valid' | 'Invalid' | 'Pending' | string;
  lastValidatedAt: string | null;
  validationErrors?: any;
}

interface ValidationEvent {
  id: string;
  sourceName: string;
  timestamp: string;
  status: 'Success' | 'Error';
  message: string;
}

interface ValidationPanelProps {
  onClose: () => void;
  dataSources: ValidationDataSource[];
  loading: boolean;
  error: boolean;
  onRefresh: () => void;
}

const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';
const DATA_VALIDATION_ICON = '/icons/validation-blue.svg';

// --- HELPER FUNCTIONS ---

const groupEventsByDate = (events: ValidationEvent[]) => {
  return events.reduce((acc: { [key: string]: ValidationEvent[] }, event) => {
    const date = parseISO(event.timestamp);
    if (!isValid(date)) return acc;
    const dateKey = format(date, 'd MMMM, yy').toUpperCase();
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);
    return acc;
  }, {});
};

const getErrorMessage = (errors: any): string => {
    const defaultMessage = 'An unknown error occurred.';
    if (!errors) {
        return defaultMessage;
    }
    if (typeof errors === 'object' && errors !== null && errors.message && typeof errors.message === 'string') {
        return errors.message;
    }
    if (typeof errors === 'object' && errors !== null && Array.isArray(errors.errors) && errors.errors.length > 0) {
        return errors.errors.map((e: any) => typeof e === 'object' && e.message ? e.message : e).join('\n');
    }
    if (typeof errors === 'string') {
        return errors;
    }
    return defaultMessage;
};

// --- COMPONENT ---

const ValidationResultPanel: React.FC<ValidationPanelProps> = ({
  onClose,
  dataSources,
  loading,
  error,
  onRefresh,
}) => {
  const theme = useTheme();
  const [filter, setFilter] = useState<'All' | 'Error'>('All');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(event.target.value as 'All' | 'Error');
      handleClose();
  }

  const validationEvents = useMemo((): ValidationEvent[] => {
    if (!dataSources) return [];

    const allEvents = dataSources
      .filter(ds => ds.lastValidatedAt && ds.validationStatus !== 'Pending')
      .map((ds): ValidationEvent => {
        const status = ds.validationStatus === 'Invalid' ? 'Error' : 'Success';
        const message = status === 'Error'
            ? getErrorMessage(ds.validationErrors)
            : 'Validation successful.';
        return {
          id: ds.id,
          sourceName: ds.name,
          timestamp: ds.lastValidatedAt!,
          status,
          message,
        };
      });

      if (filter === 'All') {
          return allEvents;
      }
      return allEvents.filter(event => event.status === 'Error');

  }, [dataSources, filter]);

  const groupedEvents = groupEventsByDate(validationEvents);
  const sortedDates = Object.keys(groupedEvents).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <ErrorDisplay
          title="Unable to Display Data"
          message="We're currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly."
          onRefresh={onRefresh}
        />
      );
    }
    
    if (validationEvents.length === 0) {
        return (
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 4, fontFamily: 'Inter', px: 3, fontSize:'14px' }}>
                No validation results found.
            </Typography>
        );
    }

    return (
       <List disablePadding>
        {sortedDates.map((date) => (
          <Box key={date} sx={{ mb: 1 }}>
            <Box sx={{ 
                display: 'flex', alignItems: 'center', color: 'text.secondary', 
                backgroundColor:' #FAFAFA', p: 2, px: 3,
            }}>
              <img src="/icons/calendar-1.svg" alt="calendar" style={{ width: '18px', height: '18px', marginRight: '9px',marginLeft:'5px', fontFamily:'12px' }} />
                  <Box sx={{ width: '100%', pl: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Inter', color: ' #333333', fontSize: '14px', lineHeight:'20px'}}>{date}</Typography>
                </Box>
            </Box>
            
            <Box sx={{ position: 'relative', px: 3, pt: 2, pb: 1 }}>
               <Box sx={{
                  position: 'absolute', left: '37px', top: 0, bottom: 0,
                  borderLeft: `2px dashed ${theme.palette.grey[300]}`,
               }}/>
              
              {groupedEvents[date].map((event) => (
                <Box key={event.id} sx={{ display: 'flex', gap: 1.5, mb: 2, position: 'relative', pl: '28px' }}>
                  <Box
                    sx={{
                      position: 'absolute', left: '0px', top: '2px',
                      width: '28px', height: '28px', borderRadius: '50%',
                      flexShrink: 0, bgcolor: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 1
                    }}
                  >
                    {event.status === 'Success' ? (
                      <img src="/icons/check-icon-vr.svg" alt="Success" style={{ width: '22px', height: '22px' }} />
                    ) : (
                      <img src="/icons/error-icon_vr.svg" alt="Error" style={{ width: '22px', height: '22px' }} />
                    )}
                  </Box>
                  <Box sx={{ width: '100%', pl: 1, mt:0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography sx={{
                        display: 'inline-block',
                        backgroundColor: 'grey.100',
                        color: 'text.primary',
                        fontWeight: 500,
                        px: 1.5,
                        py: 0.5, 
                        borderRadius: '16px', 
                        fontSize: '0.8125rem' 
                      }}>
                        {event.sourceName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Inter',mr:6 }}>
                        {format(parseISO(event.timestamp), 'h:mm a')}
                      </Typography>
                    </Box>
                    {event.status === 'Error' && (
                       <Box sx={{ mt: 1, p: 1.5, border: `1px solid ${theme.palette.grey[300]}`, borderRadius: '8px', backgroundColor: theme.palette.grey[50] }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter', whiteSpace: 'pre-wrap' }}>
                          {event.message}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </List>
    );
  };

  return (
    <Box sx={{ 
        width : '500px',
        height: '920px',
        p: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: 'white',
        borderRadius: '12px' 
    }}>
        {/* Top Header Section */}
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2, 
            flexShrink: 0,
            backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`,  
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '8px',
            p: 1,
            minHeight: '120px',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p:2, marginBottom:2 }}>
                <Box sx={{ 
                    width: 56, 
                    height: 56, 
                    borderRadius: '10px', 
                    backgroundColor: '#E5EFFF',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <img src={DATA_VALIDATION_ICON} alt="Data Source" style={{ width: 28, height: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ 
                    fontFamily: 'Inter',
                    fontWeight: 600, 
                    fontSize: '18px',
                    color: '#333333', 
                }}>
                    Validation Results
                </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#333333', mb:4, mr:8 }}>
                <CloseIcon />
            </IconButton>
        </Box>
      
        {!loading && !error && (
            <Box sx={{ px: 3, mb: 2 }}>
              <Button
                  id="status-button"
                  aria-controls={open ? 'status-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  variant="outlined"
                  onClick={handleClick}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{ textTransform: 'none', borderColor: open || filter !== 'All' ? '#0064FF' : theme.palette.grey[300], backgroundColor: open || filter !== 'All' ? '#F3F8FE' : 'transparent', color: 'text.primary', borderRadius:'8px', minWidth: '181px', height:'32px', justifyContent: 'space-between', '&:hover': { borderColor: '#0064FF' } }}
              >
                  {filter === 'All' ? 'Show: All' : 'Show: Validation issue'}
              </Button>
              <Menu
                  id="status-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  disablePortal
                  MenuListProps={{ 'aria-labelledby': 'status-button' }}
                  PaperProps={{ sx: { minWidth: '220px', boxShadow: theme.shadows[3], borderRadius: '8px', p: 1 } }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px',mb:1,marginLeft:1 }}>Select Status</Typography>
                <Divider sx={{ mb: 1, mx:-2 }} />
                          <RadioGroup value={filter} onChange={handleFilterChange} sx={{ marginLeft:1 }}>
                  <FormControlLabel value="All" control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 22 }, '&.Mui-checked': { color: '#0064FF' } }} />} label={<Typography sx={{ fontSize: '14px' }}>All</Typography>} />
                  <FormControlLabel value="Error" control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 22 }, '&.Mui-checked': { color: '#0064FF' } }} />} label={<Typography sx={{ fontSize: '14px' }}>Validation issue</Typography>} />
                </RadioGroup>
              </Menu>
            </Box>
        )}

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default ValidationResultPanel;